import { LinkButton } from '@euler/app/components/LinkButton';
import { StackLayout } from '@euler/app/components/StackLayout';
import { Typography } from '@euler/app/components/typography';
import {
  isValidVin,
  vinTransformed,
} from '@euler/app/flows/order/functions/vinHelper';
import { Icon } from '@euler/components/Icomoon';
import { Input } from '@euler/components/form/Input';
import { config } from '@euler/config';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useAlert } from '@euler/functions/ux/useAlert';
import { useServiceFactory } from '@euler/services/factory';
import { onErrorIgnore } from '@euler/utils';
import { useBehaviorSubject, usePersistFn } from '@euler/utils/hooks';
import { getVehicleAgeByVin } from '@euler/utils/vin/getVehicleAgeByVin';
import { memo, useState } from 'react';
import { ActivityIndicator } from 'react-native';
import { useOrderFlowContext } from '../../../../OrderFlowContext';
import { useCheckExistingOrder } from '../../functions/useCheckExistingOrder';
import { useOrderOcrScan } from '../../functions/useOrderOcrScan';
import { useTextInputRow } from '../../functions/useTextInputRow';
import { useVehicleInfoLoader } from '../../functions/useVehicleInfoLoader';
import { Cell } from '../components/Cell';
import { IconWrapper } from '../components/IconWrapper';
import { Row } from '../components/Row';
import { ScanButton } from '../components/ScanButton';
import { TextIcon } from '../components/TextIcon';

export const VinRow = memo(
  ({
    required = true,
    ...props
  }: {
    required?: boolean;
    disabled?: boolean;
  }) => {
    const alert = useAlert();

    const { vehicleInfoService } = useServiceFactory();

    const {
      theme,
      lastVehicleInfo$,
      formController: { form },
    } = useOrderFlowContext();

    const { rpx } = useResponsiveContext();

    const { iconColor, labelColor, placeholderColor, linkColor } = theme;
    const { onScan } = useOrderOcrScan({ type: 'vin' });
    const { inputRef, inputStyle, onCellPress } = useTextInputRow();

    const loadVehicleInfoByVIN = useVehicleInfoLoader();
    const checkExistingOrder = useCheckExistingOrder();

    const [lastVehicleInfo] = useBehaviorSubject(lastVehicleInfo$);
    const [loading, setLoading] = useState(false);

    const onBlur = usePersistFn(() => {
      form.vin.onBlur();
      const vin = form.vin.value;
      if (!isValidVin(vin)) {
        return;
      }
      if (lastVehicleInfo?.vin !== vin) {
        loadVehicleInfoByVIN(vin).catch(onErrorIgnore);
      }
      void checkExistingOrder('vin', vin);
    });

    const onGetRandomVin = usePersistFn(() => {
      setLoading(true);
      vehicleInfoService
        .getRandomRealVin()
        .then(vin => {
          if (isValidVin(vin)) {
            form.vin.update(vin);
            if (lastVehicleInfo?.vin !== vin) {
              loadVehicleInfoByVIN(vin).catch(onErrorIgnore);
            }
            void checkExistingOrder('vin', vin);
          } else {
            void alert({ title: 'Invalid VIN returned: ' + vin });
          }
        })
        .catch(err => {
          console.error(err);
          void alert({ title: 'Error', message: err.message });
        })
        .finally(() => {
          setLoading(false);
        });
    });

    // A vehicle with no ISO VIN can be neither decoded nor looked up, so the
    // row stops offering either and takes the chassis number instead. The
    // toggle that sets this lives in the section header.
    const isNonStandardVin = form.isNonStandardVin.value;

    const age = getVehicleAgeByVin(
      form.vin.value,
      form.vehicleInfo.value?.vehicleModelYear,
    );

    return (
      <Row
        errorProp={isNonStandardVin ? 'chassisNo' : 'vin'}
        icon={
          <IconWrapper>
            <TextIcon text="VIN" color={iconColor} />
          </IconWrapper>
        }
        detail={
          <StackLayout
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            gap={rpx(15)}
          >
            {loading && <ActivityIndicator size="small" />}
            {age > 0 && (
              <Typography.Text
                color="#000"
                size={rpx(10)}
                opacity={0.5}
                medium
              >{`[车龄: ${age}年]`}</Typography.Text>
            )}
            {!isNonStandardVin && (
              <ScanButton
                color={labelColor}
                onPress={onScan}
                disabled={form.vin.loading || props.disabled}
              />
            )}
          </StackLayout>
        }
        onPress={onCellPress}
        {...props}
      >
        <Cell
          label={isNonStandardVin ? '车架/地盘号' : 'VIN码'}
          required={required}
          helpTextIconHidden={true}
          helpText={
            config.environment !== 'production' && !isNonStandardVin ? (
              <LinkButton
                text="获取随机真实VIN"
                icon={<Icon name="arrow-right-circle" color={linkColor} />}
                textStyle={{ fontSize: rpx(12) }}
                onPress={onGetRandomVin}
                disabled={props.disabled}
              />
            ) : undefined
          }
        >
          {isNonStandardVin ? (
            <Input
              inputRef={inputRef}
              placeholderTextColor={placeholderColor}
              editable={!props.disabled}
              value={form.chassisNo.value ?? ''}
              placeholder="请输入车辆车架/地盘号"
              errorMsg={form.chassisNo.error}
              autoCapitalize="characters"
              clearButtonMode="while-editing"
              maxLength={32}
              style={inputStyle}
              onChangeText={s => form.chassisNo.update(s)}
              onBlur={form.chassisNo.onBlur}
              tag="chassisNo"
            />
          ) : (
            <Input
              inputRef={inputRef}
              placeholderTextColor={placeholderColor}
              editable={!props.disabled}
              value={form.vin.value}
              placeholder="请输入或扫描识别车辆VIN码"
              errorMsg={form.vin.error}
              loading={form.vin.loading}
              autoCapitalize="characters"
              keyboardType="ascii-capable"
              clearButtonMode="while-editing"
              style={inputStyle}
              onChangeText={s => form.vin.update(vinTransformed(s))}
              onBlur={onBlur}
              tag="vin"
            />
          )}
        </Cell>
      </Row>
    );
  },
);
